/**
 * `DocViewer` — the demo's right-hand document pane (`#viewer`, demo line
 * 2455) and the per-format bodies `docView` built for it.
 *
 * Six block kinds cover all seventeen of the demo's branches: a table, a
 * pre-formatted source listing, prose, markdown, a page thumbnail and a
 * notice. `doc-view.ts` decides which a document gets; this file only renders.
 *
 * Markdown goes through the assistant's `AssistantMarkdown`, which lexes with
 * `marked` and renders JSX — no HTML string ever reaches the DOM, here or
 * anywhere else in the port.
 *
 * UI layer (layer 4 of the 4-layer architecture).
 *
 * @see src/versions/v1/modules/integration/doc-view.ts
 * @see docs/logic/integration/data.md
 */

import { FileText, Sparkles } from 'lucide-react';
import { EmptyState, InlineError } from '@/design-system';
import { askOem } from '../../shared/assistant/ask';
import { AssistantMarkdown } from '../../shared/assistant/markdown';
import { DocFoot } from '../../shared/docs/DocFoot';
import { Letterhead } from '../../shared/docs/Letterhead';
import { MailSig } from '../../shared/docs/MailSig';
import { useOemData } from '../../shared/useOemData';
import type { DocBlock, DocView } from './doc-view';

const EYEBROW_CLASS = 'text-[10px] font-semibold uppercase tracking-[0.08em]';

export interface DocViewerProps {
  view: DocView | null;
  loading: boolean;
  /** Set when a reference resolved to nothing — the demo's toast, inline. */
  missingKey: string | null;
  error: Error | null;
}

export function DocViewer({ view, loading, missingKey, error }: DocViewerProps) {
  // fork(L3): the sender's organisation for an email's signature. Small and
  // usually cached by then; until it lands the signature is the OEM's.
  const people = useOemData('people');

  if (error) {
    return (
      <div className="p-4">
        <InlineError>{`The document did not load: ${error.message}`}</InlineError>
      </div>
    );
  }

  if (missingKey) {
    return (
      <EmptyState
        icon={<FileText className="mx-auto h-8 w-8 text-zinc-400" strokeWidth={1.6} />}
        title="No preview built for this file"
        description={`It ships in the data pack, but the workspace has no viewer for ${missingKey}. Pick another file from the tree.`}
      />
    );
  }

  if (loading || !view) {
    return (
      <div className="space-y-3 p-4" aria-busy="true">
        <div
          className="h-5 w-1/3 animate-pulse rounded"
          style={{ background: 'var(--surface-3)' }}
        />
        <div
          className="h-3 w-1/2 animate-pulse rounded"
          style={{ background: 'var(--surface-3)' }}
        />
        <div
          className="h-40 w-full animate-pulse rounded-lg"
          style={{ background: 'var(--surface-3)' }}
        />
      </div>
    );
  }

  // fork(L3): the demo framed a controlled document with its letterhead and
  // footer (demo line 2420) and closed an email with the sender's signature
  // (demo line 2421). Both are read off the metadata `doc-view.ts` already
  // builds, so the resolver is untouched.
  const letter = letterheadFor(view);
  const mailOrg =
    metaValue(view, 'Type') === 'Email'
      ? (people.data?.people.find((p) => p.name === metaValue(view, 'Author')?.split(' → ')[0])
          ?.org ?? undefined)
      : null;

  return (
    <article className="min-w-0 space-y-4 p-4">
      {letter ? <Letterhead {...letter.head} /> : null}
      <header>
        <h2
          className="text-[15px] font-semibold leading-tight tracking-tight"
          style={{ color: 'var(--ink-primary)' }}
        >
          {view.title}
        </h2>
        <p className="mono mt-0.5 text-[11px]" style={{ color: 'var(--ink-tertiary)' }}>
          {view.path}
        </p>
      </header>

      <section
        className="rounded-xl border p-3"
        style={{ borderColor: 'var(--hairline)', background: 'var(--surface-2)' }}
      >
        <h3 className={EYEBROW_CLASS} style={{ color: 'var(--ink-tertiary)' }}>
          Document metadata
        </h3>
        <dl className="mt-2 grid grid-cols-[minmax(0,auto)_minmax(0,1fr)] gap-x-3 gap-y-1 text-[11.5px]">
          {view.meta.map((row) => (
            <div key={row.label} className="contents">
              <dt style={{ color: 'var(--ink-tertiary)' }}>{row.label}</dt>
              <dd
                className={row.mono ? 'mono min-w-0 break-words' : 'min-w-0 break-words'}
                style={{ color: 'var(--ink-secondary)' }}
              >
                {row.value || '—'}
              </dd>
            </div>
          ))}
        </dl>
      </section>

      {view.blocks.map((block, i) => (
        <BlockPanel key={`${block.kind}-${i}`} block={block} />
      ))}
      {mailOrg !== null ? <MailSig org={mailOrg} /> : null}
      {letter ? <DocFoot docId={letter.reference} controlled={letter.controlled} /> : null}
    </article>
  );
}

const metaValue = (view: DocView, label: string): string | undefined =>
  view.meta.find((row) => row.label === label)?.value;

/**
 * Demo line 2420 — the letterhead arguments for a controlled library document,
 * or `null` for every other kind of file.
 */
function letterheadFor(view: DocView) {
  if (metaValue(view, 'Type') !== 'Word document') return null;
  const reference = metaValue(view, 'Reference') ?? '';
  const [docId, rev = ''] = reference.split(' Rev ');
  const date = metaValue(view, 'Date');
  return {
    head: {
      docType: metaValue(view, 'Document') ?? 'Technical document',
      docId,
      rev,
      date: date && /^20\d\d-\d\d-\d\d$/.test(date) ? date : '',
      classification: metaValue(view, 'Classification'),
      unit: /^MI-/.test(docId) ? 'Adopted from Mount Isolde Copper — Geotechnical' : null,
    },
    reference,
    controlled: !/^(WO|IR)-/.test(docId),
  };
}

function BlockPanel({ block }: { block: DocBlock }) {
  return (
    <section
      className="min-w-0 rounded-xl border p-3"
      style={{ borderColor: 'var(--hairline)', background: 'var(--surface-2)' }}
    >
      <h3 className={EYEBROW_CLASS} style={{ color: 'var(--ink-tertiary)' }}>
        {block.eyebrow}
      </h3>
      <div className="mt-2 min-w-0">
        <BlockBody block={block} />
      </div>
    </section>
  );
}

function BlockBody({ block }: { block: DocBlock }) {
  switch (block.kind) {
    case 'table':
      return (
        <>
          <div
            className="scroll-soft max-h-[420px] overflow-auto rounded-lg"
            style={{ border: '1px solid var(--hairline)' }}
          >
            <table className="w-full" style={{ fontSize: 11.5, borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  {block.cols.map((col) => (
                    <th
                      key={col}
                      scope="col"
                      className="px-2.5 py-1.5 text-left"
                      style={{
                        position: 'sticky',
                        top: 0,
                        background: 'var(--surface-1)',
                        color: 'var(--ink-tertiary)',
                        borderBottom: '1px solid var(--hairline)',
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {block.rows.map((row, i) => (
                  <tr key={i}>
                    {row.map((cell, j) => (
                      <td
                        key={j}
                        className={typeof cell === 'number' ? 'mono px-2.5 py-1.5 text-right' : 'px-2.5 py-1.5'}
                        style={{
                          color: 'var(--ink-secondary)',
                          borderTop: i === 0 ? undefined : '1px solid var(--hairline)',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {cell === null || cell === '' ? '—' : String(cell)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {block.note ? (
            <p className="mt-2 text-[11px]" style={{ color: 'var(--ink-tertiary)' }}>
              {block.note}
            </p>
          ) : null}
        </>
      );

    case 'code':
      return (
        <pre
          className="mono scroll-soft max-h-[420px] overflow-auto whitespace-pre-wrap rounded-lg p-3"
          style={{
            background: 'var(--surface-1)',
            border: '1px solid var(--hairline)',
            color: 'var(--ink-secondary)',
            fontSize: 11.5,
            lineHeight: 1.6,
          }}
        >
          {block.text}
        </pre>
      );

    case 'text':
      return (
        <p
          className="scroll-soft max-h-[520px] overflow-auto whitespace-pre-wrap text-[12.5px] leading-[1.65]"
          style={{ color: 'var(--ink-secondary)' }}
        >
          {block.text}
        </p>
      );

    case 'markdown':
      return (
        <div className="scroll-soft max-h-[520px] overflow-auto">
          <AssistantMarkdown source={block.source} />
        </div>
      );

    case 'thumb':
      return (
        <img
          src={`data:image/webp;base64,${block.base64}`}
          alt="Page 1"
          loading="lazy"
          className="mx-auto max-h-[460px] w-auto rounded-lg"
          style={{ border: '1px solid var(--hairline)' }}
        />
      );

    case 'notice':
      return (
        <>
          <p className="text-[12.5px] leading-[1.6]" style={{ color: 'var(--ink-secondary)' }}>
            {block.text}
          </p>
          {block.ask ? (
            <button
              type="button"
              onClick={() => askOem(block.ask as string)}
              className="mt-2 inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-left text-[12px] transition-colors hover:bg-[var(--surface-1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--accent)_55%,transparent)]"
              style={{ borderColor: 'var(--hairline)', color: 'var(--ink-primary)' }}
            >
              <Sparkles className="h-3 w-3 shrink-0" style={{ color: 'var(--accent)' }} aria-hidden="true" />
              <span>{`“${block.ask}”`}</span>
            </button>
          ) : null}
        </>
      );

    default:
      return null;
  }
}
